import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSubscription } from "@/hooks/useSubscription";

interface PaywallProps {
  feature: string;
  description?: string;
  children: ReactNode;
  /** When true, render nothing instead of the upgrade card if locked */
  silent?: boolean;
}

export function Paywall({ feature, description, children, silent }: PaywallProps) {
  const { hasAccess, loading } = useSubscription();
  if (loading) return null;
  if (hasAccess) return <>{children}</>;
  if (silent) return null;
  return (
    <Card className="border-primary/30 bg-card">
      <CardContent className="py-10 px-6 text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Lock className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{feature} is a paid feature</h3>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        <Link to="/pricing">
          <Button>Start 14-day free trial</Button>
        </Link>
        <p className="text-xs text-muted-foreground">Cancel anytime. Keep access until end of period.</p>
      </CardContent>
    </Card>
  );
}

/** Inline lock for buttons / list items. */
export function useHasAccess() {
  const { hasAccess, loading } = useSubscription();
  return { hasAccess, loading };
}